import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";

import CancelIcon from "@mui/icons-material/Cancel";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useCvStepNavigation } from "../../../contexts/CvStepNavigationContext";
import { usePatchCvMutation } from "../../../lib/cvsSlice";

// ── Date helpers ──────────────────────────────────────────────────────────────
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function toIsoDate(issueMonth, issueYear) {
  if (!issueYear) return "";
  const idx = MONTHS.indexOf(issueMonth);
  const month = idx >= 0 ? String(idx + 1).padStart(2, "0") : "01";
  return `${issueYear}-${month}-01`;
}

function fromIsoDate(dateStr) {
  if (!dateStr) return { issueMonth: "", issueYear: "" };
  const [year, month] = dateStr.split("-");
  return {
    issueMonth: MONTHS[parseInt(month, 10) - 1] ?? "",
    issueYear: year ?? "",
  };
}

function formatDisplayDate(issueMonth, issueYear) {
  if (!issueYear) return "";
  const idx = MONTHS.indexOf(issueMonth);
  const month = idx >= 0 ? String(idx + 1).padStart(2, "0") : "01";
  return `01/${month}/${issueYear}`;
}

// ── Constants ─────────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  name: "",
  certId: "",
  issueDate: "",
  issuer: "",
  linkToCert: "",
  expirable: false,
};

// ── Shared form fields (used in both inline and modal) ────────────────────────
const CertForm = ({ form, setField }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
    <TextField
      label="Certificate Name"
      placeholder="Enter Name"
      value={form.name}
      onChange={(e) => setField("name", e.target.value)}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      label="Certificate Identification"
      placeholder="Enter Identification"
      value={form.certId}
      onChange={(e) => setField("certId", e.target.value)}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      label="Issue Date"
      type="date"
      value={form.issueDate}
      onChange={(e) => setField("issueDate", e.target.value)}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
      inputProps={{ max: new Date().toISOString().split("T")[0] }}
    />
    <TextField
      label="Issuing Institute"
      placeholder="Enter Name"
      value={form.issuer}
      onChange={(e) => setField("issuer", e.target.value)}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
    />
    <TextField
      label="Link to Certificate"
      placeholder="Enter Link"
      value={form.linkToCert}
      onChange={(e) => setField("linkToCert", e.target.value)}
      fullWidth
      variant="outlined"
      size="small"
      InputLabelProps={{ shrink: true }}
    />
    <FormControlLabel
      label={<Typography sx={{ fontSize: "14px", color: "#3D3634" }}>Expired</Typography>}
      control={
        <Checkbox
          checked={form.expirable}
          onChange={(e) => setField("expirable", e.target.checked)}
          sx={{ color: "#C5BFBE", "&.Mui-checked": { color: "#1A1A1A" } }}
        />
      }
    />
  </Box>
);

// ── Main component ────────────────────────────────────────────────────────────
const Certifications = ({ certs, cvId }) => {
  const [localCerts, setLocalCerts] = useState(Array.isArray(certs) ? certs : []);
  const [form, setForm] = useState(EMPTY_FORM);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalForm, setModalForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalSaving, setIsModalSaving] = useState(false);
  const isDirty = useRef(false);

  const [patchCv] = usePatchCvMutation();
  const { goToNextStep, goToPrevStep } = useCvStepNavigation();

  // Guard against 1-second CvDetails poll resetting local state
  useEffect(() => {
    if (isDirty.current) return;
    setLocalCerts(Array.isArray(certs) ? certs : []);
  }, [certs, cvId]);

  const setField = (key, value) => {
    isDirty.current = true;
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const setModalField = (key, value) => {
    isDirty.current = true;
    setModalForm((prev) => ({ ...prev, [key]: value }));
  };

  const buildPayload = (f) => {
    const { issueMonth, issueYear } = fromIsoDate(f.issueDate);
    return {
      name: f.name.trim(),
      certId: f.certId.trim(),
      issueMonth,
      issueYear,
      issuer: f.issuer.trim(),
      linkToCert: f.linkToCert.trim(),
      expirable: f.expirable,
    };
  };

  const validate = (f) => {
    if (!f.name.trim())   { toast.error("Certificate name is required"); return false; }
    if (!f.issueDate)     { toast.error("Issue date is required"); return false; }
    if (!f.issuer.trim()) { toast.error("Issuing institute is required"); return false; }
    return true;
  };

  // ── Inline form save (first cert, no modal) ───────────────────────────────
  const handleInlineSave = async () => {
    if (!validate(form)) return;
    setIsSaving(true);
    try {
      const result = await patchCv({
        cvId,
        certifications: [buildPayload(form)],
      }).unwrap();
      setLocalCerts(result?.certifications ?? localCerts);
      setForm(EMPTY_FORM);
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Modal: open for add ───────────────────────────────────────────────────
  const openAddModal = () => {
    setEditingId(null);
    setModalForm(EMPTY_FORM);
    setModalOpen(true);
  };

  // ── Modal: open for edit ──────────────────────────────────────────────────
  const openEditModal = (cert) => {
    isDirty.current = true;
    setEditingId(cert._id);
    setModalForm({
      name: cert.name ?? "",
      certId: cert.certId ?? "",
      issueDate: toIsoDate(cert.issueMonth, cert.issueYear),
      issuer: cert.issuer ?? "",
      linkToCert: cert.linkToCert ?? "",
      expirable: cert.expirable ?? false,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setModalForm(EMPTY_FORM);
    setEditingId(null);
  };

  // ── Modal: ADD (save + close) ─────────────────────────────────────────────
  const handleModalAdd = async () => {
    if (!validate(modalForm)) return;
    setIsModalSaving(true);
    try {
      const payload = buildPayload(modalForm);
      const result = editingId
        ? await patchCv({ cvId, certifications: [{ id: editingId, ...payload }] }).unwrap()
        : await patchCv({ cvId, certifications: [payload] }).unwrap();
      setLocalCerts(result?.certifications ?? localCerts);
      closeModal();
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsModalSaving(false);
    }
  };

  // ── Modal: SAVE & ADD ANOTHER (save + reset, keep modal open) ────────────
  const handleSaveAndAddAnother = async () => {
    if (!validate(modalForm)) return;
    setIsModalSaving(true);
    try {
      const payload = buildPayload(modalForm);
      const result = editingId
        ? await patchCv({ cvId, certifications: [{ id: editingId, ...payload }] }).unwrap()
        : await patchCv({ cvId, certifications: [payload] }).unwrap();
      setLocalCerts(result?.certifications ?? localCerts);
      setModalForm(EMPTY_FORM);
      setEditingId(null);
    } catch (err) {
      toast.error(`Failed to save: ${err?.data?.message ?? err.message}`);
    } finally {
      setIsModalSaving(false);
    }
  };

  // ── Delete cert ───────────────────────────────────────────────────────────
  const handleDelete = async (certId) => {
    isDirty.current = true;
    try {
      await patchCv({ cvId, certifications: [{ id: certId, delete: true }] }).unwrap();
      setLocalCerts((prev) => prev.filter((c) => c._id !== certId));
    } catch (err) {
      toast.error(`Failed to remove: ${err?.data?.message ?? err.message}`);
    }
  };

  // ── Exclude in CV toggle ──────────────────────────────────────────────────
  const handleExcludeToggle = async (cert, newValue) => {
    isDirty.current = true;
    setLocalCerts((prev) =>
      prev.map((c) => (c._id === cert._id ? { ...c, excludeInCv: newValue } : c))
    );
    try {
      await patchCv({ cvId, certifications: [{ id: cert._id, excludeInCv: newValue }] }).unwrap();
    } catch (err) {
      setLocalCerts((prev) =>
        prev.map((c) => (c._id === cert._id ? { ...c, excludeInCv: cert.excludeInCv } : c))
      );
      toast.error("Failed to update exclude setting");
    }
  };

  const hasCerts = localCerts.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>

      {/* ── STATE A: No certs yet — show inline form ── */}
      {!hasCerts && (
        <>
          <CertForm form={form} setField={setField} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box /> {/* spacer */}
            <Button
              variant="contained"
              onClick={handleInlineSave}
              disabled={isSaving}
              sx={{
                backgroundColor: "#1A1A1A",
                color: "#fff",
                "&:hover": { backgroundColor: "#3D3634" },
                "&:disabled": { backgroundColor: "#C5BFBE" },
                borderRadius: "6px",
                px: 4,
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              {isSaving ? "Saving..." : "SAVE"}
            </Button>
          </Box>
        </>
      )}

      {/* ── STATE B: Certs exist — show list + ADD ANOTHER ── */}
      {hasCerts && (
        <>
          {localCerts.map((cert) => (
            <Box
              key={cert._id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1.5,
                border: "1px solid #E0DEDD",
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: "#1A1A1A",
                    fontFamily: "Inter, sans-serif",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {cert.name}
                </Typography>
                <Typography sx={{ fontSize: "12px", color: "#7A706F", fontFamily: "Inter, sans-serif" }}>
                  {formatDisplayDate(cert.issueMonth, cert.issueYear)}
                  {" - "}
                  {cert.expirable ? "Expired" : "Present"}
                </Typography>
              </Box>

              <IconButton
                size="small"
                onClick={() => openEditModal(cert)}
                sx={{ color: "#9E9E9E", "&:hover": { color: "#1A1A1A" } }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>

              <IconButton
                size="small"
                onClick={() => handleDelete(cert._id)}
                sx={{ color: "#9E9E9E", "&:hover": { color: "#E84610" } }}
              >
                <CancelIcon fontSize="small" />
              </IconButton>

              <Tooltip title="Exclude this certificate from exported CV">
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                  <Switch
                    size="small"
                    checked={cert.excludeInCv ?? false}
                    onChange={(e) => handleExcludeToggle(cert, e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: "#1A1A1A" },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#1A1A1A" },
                    }}
                  />
                  <Typography sx={{ fontSize: "11px", color: "#7A706F", whiteSpace: "nowrap" }}>
                    Exclude in CV
                  </Typography>
                </Box>
              </Tooltip>
            </Box>
          ))}

          {/* Add another */}
          <Box
            component="button"
            onClick={openAddModal}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              background: "none",
              border: "none",
              cursor: "pointer",
              p: 0,
              width: "fit-content",
              color: "#1A1A1A",
              fontFamily: "Inter, sans-serif",
              fontWeight: 600,
              fontSize: "13px",
              letterSpacing: "0.5px",
              textTransform: "uppercase",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            + ADD ANOTHER
          </Box>
        </>
      )}

      {/* Navigation — always visible */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
        <Button
          variant="outlined"
          onClick={goToPrevStep}
          sx={{
            borderColor: "#C5BFBE",
            color: "#3D3634",
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
            "&:hover": { borderColor: "#9E9E9E", backgroundColor: "#F4F2F1" },
          }}
        >
          BACK
        </Button>

        <Button
          variant="contained"
          onClick={goToNextStep}
          sx={{
            backgroundColor: "#E84610",
            "&:hover": { backgroundColor: "#C73A0D" },
            borderRadius: "6px",
            px: 3,
            fontWeight: 600,
            letterSpacing: "0.5px",
          }}
        >
          NEXT
        </Button>
      </Box>

      {/* ── Modal (Add / Edit) ──────────────────────────────────────────────── */}
      <Dialog
        open={modalOpen}
        onClose={closeModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1,
            fontFamily: "Inter, sans-serif",
            fontWeight: 600,
            fontSize: "16px",
          }}
        >
          {editingId ? "Edit License and Certificate" : "Add License and Certificates"}
          <IconButton onClick={closeModal} size="small" sx={{ color: "#9E9E9E" }}>
            <CancelIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: "8px !important" }}>
          <CertForm form={modalForm} setField={setModalField} />

          {/* Modal actions */}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 1 }}>
            <Box
              component="button"
              onClick={handleSaveAndAddAnother}
              disabled={isModalSaving}
              sx={{
                background: "none",
                border: "none",
                cursor: "pointer",
                p: 0,
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                color: "#1A1A1A",
                fontFamily: "Inter, sans-serif",
                fontWeight: 600,
                fontSize: "13px",
                letterSpacing: "0.5px",
                textTransform: "uppercase",
                "&:hover": { textDecoration: "underline" },
                "&:disabled": { color: "#C5BFBE", cursor: "not-allowed" },
              }}
            >
              + SAVE &amp; ADD ANOTHER
            </Box>

            <Button
              variant="contained"
              onClick={handleModalAdd}
              disabled={isModalSaving}
              sx={{
                backgroundColor: "#E84610",
                "&:hover": { backgroundColor: "#C73A0D" },
                "&:disabled": { backgroundColor: "#E8A090" },
                borderRadius: "6px",
                px: 4,
                fontWeight: 600,
                letterSpacing: "0.5px",
              }}
            >
              {isModalSaving ? "Saving..." : editingId ? "UPDATE" : "ADD"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Certifications;
